/** Protocols analyze_media can speak, in preference order (richest native first).
 *  Deliberately EXCLUDES anthropic_messages — the Anthropic protocol has no video
 *  content block (#333), so a vision/video tool must use one of these instead. */
export const TOOL_VISION_PROTOCOLS = ["gemini", "openai_chat_completions"];
/** analyze_media 能为 PDF 构造 document part 的协议面。
 *  新增一条 = 新增一段发送代码 + 一次带暗号的活体验证，两者缺一不许进这个集合。 */
export const DOCUMENT_SEND_LEGS = new Set(["gemini", "openai_chat_completions"]);
/**
 * 生成类按协议行 `endpointPath` 筛，**不按 `protocol_id`**。
 * 生成类模型的 protocol_id 与真实调用路径脱钩（v78：`grok-imagine-image-2.0` /
 * `gpt-image-2` 标着 `openai_chat_completions`，真实打的是 Images / Videos API）。
 * 协议白名单是对上游形状的猜测，endpoint 是清单预计算的事实。
 */
export const IMAGE_GENERATIONS_ENDPOINT = "/v1/images/generations";
export const VIDEO_GENERATIONS_ENDPOINT = "/v1/videos/generations";
/** 生图 / 生视频端点。判据是能力形状，不是模型名单。 */
export const MEDIA_GENERATION_ENDPOINTS = new Set([
    IMAGE_GENERATIONS_ENDPOINT,
    VIDEO_GENERATIONS_ENDPOINT,
]);
export function isMediaGenerationEndpoint(endpointPath) {
    return typeof endpointPath === "string" && MEDIA_GENERATION_ENDPOINTS.has(endpointPath);
}
function generationGate(options) {
    return options?.isGenerationEndpoint ?? isMediaGenerationEndpoint;
}
/**
 * 读 PDF 进池的网关能力字段：`formats ∋ "pdf"` **且** `scannedPdf === true` **且**
 * 协议行 `carriesDocumentInput === true`。三者缺一（含缺字段）都不进池。
 * 这只是字段闸；发送形状还要过 {@link DOCUMENT_SEND_LEGS}。
 */
export function catalogRowCarriesPdfDocument(row) {
    return (row.documentInput?.formats?.includes("pdf") === true
        && row.documentInput?.scannedPdf === true
        && row.carriesDocumentInput === true);
}
/**
 * 能力字段做准入。缺席的 bool = 未知，不进池。一律 `=== true`，不用 truthy。
 * ① 生视频必须 `supportsTextPrompt === true`。
 */
export function catalogRowAdmitsImageGeneration(row) {
    return row.capabilities?.supportsImageGeneration === true
        && row.endpointPath === IMAGE_GENERATIONS_ENDPOINT;
}
export function catalogRowAdmitsImageEditing(row) {
    return row.capabilities?.supportsImageEditing === true
        && row.endpointPath === IMAGE_GENERATIONS_ENDPOINT;
}
export function catalogRowAdmitsVideoGeneration(row) {
    return row.capabilities?.supportsVideoGeneration === true
        && row.capabilities?.supportsTextPrompt === true
        && row.endpointPath === VIDEO_GENERATIONS_ENDPOINT;
}
export function catalogRowAdmitsVideoEditing(row) {
    return row.capabilities?.supportsVideoEditing === true
        && row.endpointPath === VIDEO_GENERATIONS_ENDPOINT;
}
/** 行级：看图理解。`supportsVision === true` 且不是生成端点。不含协议腿。 */
export function catalogRowAdmitsVisionUnderstanding(row, options) {
    return row.capabilities?.supportsVision === true
        && !generationGate(options)(row.endpointPath);
}
const SPEAKABLE = new Set(TOOL_VISION_PROTOCOLS);
function pickSpeakable(rows) {
    const byProtocol = new Map();
    for (const row of rows)
        byProtocol.set(row.protocol, row);
    const chosen = TOOL_VISION_PROTOCOLS.find((protocol) => byProtocol.has(protocol));
    return chosen ? byProtocol.get(chosen) : undefined;
}
function fail(reason, detail) {
    return detail === undefined ? { ok: false, reason } : { ok: false, reason, detail };
}
function rowsForModel(catalog, modelId) {
    return catalog.filter((row) => row.modelId === modelId);
}
function admitBySpeakableCapability(mine, args) {
    const capable = mine.filter(args.capable);
    if (capable.length === 0) {
        return fail("capability-missing", args.capabilityDetail);
    }
    const remaining = args.applyGenerationGate
        ? capable.filter((row) => !generationGate(args.options)(row.endpointPath))
        : capable;
    if (args.applyGenerationGate && remaining.length === 0) {
        return fail("generation-endpoint", args.generationDetail);
    }
    const speakable = remaining.filter(args.speakable);
    if (speakable.length === 0) {
        const protocols = [...new Set(remaining.map((row) => row.protocol))].join("/") || "无";
        return fail("no-speakable-leg", `无可用协议行：现有 ${protocols}`);
    }
    const picked = args.applyGenerationGate ? pickSpeakable(speakable) : speakable[0];
    if (!picked) {
        const protocols = [...new Set(remaining.map((row) => row.protocol))].join("/") || "无";
        return fail("no-speakable-leg", `无可用协议行：现有 ${protocols}`);
    }
    return { ok: true, row: picked };
}
function admitGeneration(mine, admits, capabilityDetail) {
    const hit = mine.find(admits);
    if (!hit)
        return fail("capability-missing", capabilityDetail);
    return { ok: true, row: hit };
}
/**
 * 问一个 modelId 为什么进 / 不进某个工具桶。
 *
 * 理解类在行级丢掉生成端点后再挑 {@link TOOL_VISION_PROTOCOLS}：
 * 混合模型（chat 行 + images 行）只丢 images 那条。
 */
export function admitToolModel(catalog, modelId, bucket, options) {
    const mine = rowsForModel(catalog, modelId);
    if (mine.length === 0)
        return fail("not-in-catalog");
    switch (bucket) {
        case "vision":
            return admitBySpeakableCapability(mine, {
                capable: (row) => row.capabilities?.supportsVision === true,
                applyGenerationGate: true,
                speakable: (row) => SPEAKABLE.has(row.protocol),
                capabilityDetail: "没有任何一行 supportsVision === true",
                generationDetail: "协议行 endpointPath 指向生图/生视频端点，不能做理解",
                options,
            });
        case "video-understand":
            return admitBySpeakableCapability(mine, {
                capable: (row) => row.capabilities?.supportsVision === true
                    && row.capabilities?.supportsVideo === true,
                applyGenerationGate: true,
                speakable: (row) => SPEAKABLE.has(row.protocol),
                capabilityDetail: "没有任何一行 supportsVision === true 且 supportsVideo === true",
                generationDetail: "协议行 endpointPath 指向生图/生视频端点，不能做理解",
                options,
            });
        case "document":
            return admitBySpeakableCapability(mine, {
                capable: catalogRowCarriesPdfDocument,
                applyGenerationGate: false,
                speakable: (row) => DOCUMENT_SEND_LEGS.has(row.protocol),
                capabilityDetail: "没有任何一行 formats∋pdf 且 scannedPdf===true 且 carriesDocumentInput===true",
                generationDetail: "",
                options,
            });
        case "image-generation":
            return admitGeneration(mine, catalogRowAdmitsImageGeneration, "无生成类准入行（能力字段或 endpointPath 不符）");
        case "image-editing":
            return admitGeneration(mine, catalogRowAdmitsImageEditing, "无生成类准入行（能力字段或 endpointPath 不符）");
        case "video-generation":
            return admitGeneration(mine, catalogRowAdmitsVideoGeneration, "无生成类准入行（能力字段或 endpointPath 不符）");
        case "video-editing":
            return admitGeneration(mine, catalogRowAdmitsVideoEditing, "无生成类准入行（能力字段或 endpointPath 不符）");
    }
}
//# sourceMappingURL=tool-admission.js.map